import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { X } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { displayValue, draftValue, tidyDraft } from '../lib/number-field.js';
import { useSwipeToDismiss } from '../lib/swipe.js';

/**
 * Join class names, letting later ones win over earlier conflicts.
 *
 * Plain concatenation does not work here: a component's base classes and a
 * caller's override both end up in the string, and CSS then resolves by
 * declaration order in the stylesheet rather than by what the caller intended.
 * That is how `<Select className="w-40">` silently stayed full width.
 */
/**
 * Makes a small text link comfortably clickable without moving anything.
 *
 * A link sitting in a line of 12px text is about sixteen pixels tall, which
 * leaves eight pixels either side of where somebody aims. People push down
 * slightly as they press, so the release lands outside the element — and a
 * browser only raises a click when the press and the release share a target.
 * The result is a link that ignores every second or third attempt, which is
 * precisely how it was reported.
 *
 * A transparent pseudo-element does the catching, so the hit area grows while
 * the text stays exactly where it was. Six pixels each way roughly doubles the
 * height without reaching the line above or below.
 */
export const ROOMY_TARGET = "relative after:absolute after:-inset-x-1.5 after:-inset-y-1.5 after:content-['']";

export function cx(...parts: (string | false | null | undefined)[]): string {
  return twMerge(parts.filter(Boolean).join(' '));
}

// --- Button ----------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-crust-600 text-white hover:bg-crust-700 active:bg-crust-800 disabled:bg-crust-300',
  secondary:
    'bg-[var(--color-surface)] text-[var(--color-ink)] border border-[var(--color-line-strong)] hover:bg-[var(--color-surface-sunken)]',
  ghost: 'text-[var(--color-ink-soft)] hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)]',
  danger: 'bg-[var(--color-danger)] text-white hover:brightness-110',
  success: 'bg-[var(--color-ok)] text-white hover:brightness-110',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  // Generous hit areas: this is used on a tablet, by someone with flour on their hands.
  sm: 'h-9 px-3 text-sm gap-1.5',
  md: 'h-11 px-4 text-sm gap-2',
  lg: 'h-14 px-6 text-base gap-2.5',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  loading?: boolean;
  full?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  loading,
  full,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center rounded-[var(--radius-md)] font-medium transition',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-crust-500',
        'disabled:cursor-not-allowed disabled:opacity-60',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        full && 'w-full',
        className,
      )}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cx('inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent', className)}
      aria-hidden
    />
  );
}

// --- Surfaces --------------------------------------------------------------

export function Card({ className, children, ...rest }: { className?: string; children: ReactNode } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...rest} className={cx('card', className)}>
      {children}
    </div>
  );
}

export function SectionHeading({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="mb-3 flex items-end justify-between gap-4">
      <div>
        <h2 className="font-display text-lg font-semibold text-[var(--color-ink)]">{title}</h2>
        {hint && <p className="mt-0.5 text-sm text-[var(--color-ink-soft)]">{hint}</p>}
      </div>
      {action}
    </div>
  );
}

// --- Badge -----------------------------------------------------------------

type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'brand';

const TONES: Record<Tone, string> = {
  neutral: 'bg-[var(--color-surface-sunken)] text-[var(--color-ink-soft)]',
  ok: 'bg-[var(--color-ok-soft)] text-[var(--color-ok)]',
  warn: 'bg-[var(--color-warn-soft)] text-[var(--color-warn)]',
  danger: 'bg-[var(--color-danger-soft)] text-[var(--color-danger)]',
  info: 'bg-[var(--color-info-soft)] text-[var(--color-info)]',
  brand: 'bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]',
};

export function Badge({
  tone = 'neutral',
  children,
  color,
  className,
}: {
  tone?: Tone;
  color?: string;
  children: ReactNode;
  className?: string;
}) {
  // An explicit colour comes from operator-defined data (an order status), so it
  // is applied inline rather than through a class the build cannot know about.
  const style = color
    ? { backgroundColor: `${color}1f`, color, borderColor: `${color}40` }
    : undefined;

  return (
    <span
      style={style}
      className={cx(
        'inline-flex items-center gap-1 rounded-full border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        !color && TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

// --- Fields ----------------------------------------------------------------

export function Field({
  label,
  hint,
  error,
  required,
  className,
  children,
}: {
  label?: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  /** For grid placement, and for `min-w-0` where a field must be allowed to shrink. */
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={cx('block', className)}>
      {label && (
        <span className="mb-1.5 block text-sm font-medium text-[var(--color-ink)]">
          {label}
          {required && <span className="ml-0.5 text-[var(--color-danger)]">*</span>}
        </span>
      )}
      {children}
      {error ? (
        <span className="mt-1 block text-xs text-[var(--color-danger)]">{error}</span>
      ) : (
        hint && <span className="mt-1 block text-xs text-[var(--color-ink-faint)]">{hint}</span>
      )}
    </label>
  );
}

const FIELD_BASE =
  'w-full rounded-[var(--radius-md)] border border-[var(--color-line-strong)] bg-[var(--color-surface)] px-3 text-sm text-[var(--color-ink)] ' +
  'placeholder:text-[var(--color-ink-faint)] focus:border-crust-500 focus:outline-2 focus:outline-offset-0 focus:outline-crust-500/40 disabled:opacity-60';

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={cx(FIELD_BASE, 'h-11', className)} />;
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...rest} className={cx(FIELD_BASE, 'py-2.5 leading-relaxed', className)} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...rest} className={cx(FIELD_BASE, 'h-11 pr-8', className)}>
      {children}
    </select>
  );
}

/**
 * A box holding a plain number — a quantity, a count, a percentage.
 *
 * Selects what it holds when you reach it, so the first digit you type
 * replaces the old value instead of landing next to it. That is the whole
 * point: tapping a quantity reading `0` and typing `2` should leave `2`, not
 * `02`, and should not require selecting the old value by hand on every row
 * of a recipe.
 *
 * It keeps what you typed as text until you leave, which is what makes `0.`
 * on the way to `0.5` possible, and which also sidesteps React leaving a
 * stale `02` on screen over a correctly-stored 2. See `lib/number-field`.
 *
 * Deliberately a text box with a numeric keypad rather than `type="number"`:
 * the spinner arrows sit right where a thumb lands on a crowded row, and a
 * number input quietly changes under a scroll wheel. Phones still get the
 * number pad from `inputMode`.
 */
export function NumberInput({
  value,
  onChange,
  onClear,
  className,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value: number | '';
  onChange: (value: number) => void;
  /**
   * What to do when the box is emptied. Without this, clearing it stores 0 —
   * right for a quantity. Fields where empty means something other than zero
   * ("keeps indefinitely") pass this to say so.
   */
  onClear?: () => void;
}) {
  const [draft, setDraft] = useState(() => displayValue(value));
  const focused = useRef(false);

  /*
   * Follow the stored value, but never over someone's fingers mid-edit.
   *
   * While the box has focus there are two ways the value can move. One is the
   * person typing, where the stored value is only an echo of the draft and
   * rewriting it would fight them — including the moment the box is empty,
   * which means "still deciding", not nought. The other is something outside
   * changing it, like the plus button beside a cart line; that one has to win,
   * or the box sits showing 24 while the total has moved on to 25.
   *
   * Telling them apart is just asking whether the stored value is still what
   * the draft says.
   */
  useEffect(() => {
    const typed = draftValue(draft);
    if (focused.current && (typed === value || typed === null)) return;
    setDraft(displayValue(value));
  }, [value, draft]);

  return (
    <input
      {...rest}
      type="text"
      inputMode={rest.inputMode ?? 'decimal'}
      value={draft}
      onFocus={(event) => {
        focused.current = true;
        event.target.select();
        rest.onFocus?.(event);
      }}
      onBlur={(event) => {
        focused.current = false;
        // Settle back to the stored value, so a half-typed '0.' or a cleared
        // box does not stay on screen looking like a quantity.
        setDraft(displayValue(value));
        rest.onBlur?.(event);
      }}
      onChange={(event) => {
        const text = tidyDraft(event.target.value);
        setDraft(text);
        const next = draftValue(text);
        if (next !== null) onChange(next);
        else if (text === '') onClear?.();
      }}
      className={cx(FIELD_BASE, 'h-11', className)}
    />
  );
}

/**
 * Money input. Shows pesos, stores centavos, and never lets a float near the
 * value that ends up on a receipt.
 */
export function MoneyInput({
  value,
  onChange,
  className,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: number;
  onChange: (centavos: number) => void;
}) {
  const [text, setText] = useState(() => (value / 100).toFixed(2));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setText((value / 100).toFixed(2));
  }, [value]);

  return (
    <div className="relative">
      <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-[var(--color-ink-faint)]">₱</span>
      <input
        {...rest}
        inputMode="decimal"
        value={text}
        onFocus={(event) => {
          focused.current = true;
          // Same reason as NumberInput: typing should replace the 0.00 sitting
          // there, not append to it. Matters most on the till's tendered box.
          event.target.select();
        }}
        onBlur={() => {
          focused.current = false;
          setText((value / 100).toFixed(2));
        }}
        onChange={(event) => {
          const next = event.target.value;
          setText(next);
          const parsed = Number.parseFloat(next.replace(/[^\d.-]/g, ''));
          onChange(Number.isFinite(parsed) ? Math.round(parsed * 100) : 0);
        }}
        className={cx(FIELD_BASE, 'tabular h-11 pl-7 text-right', className)}
      />
    </div>
  );
}

// --- Modal -----------------------------------------------------------------

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const labelId = useId();
  const dismissSwipe = useSwipeToDismiss(onClose);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Stop the page behind the dialog from scrolling with it.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-crust-900/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        className={cx(
          'relative flex max-h-[92vh] w-full flex-col rounded-t-[var(--radius-xl)] sm:rounded-[var(--radius-xl)]',
          'bg-[var(--color-surface)] elevation-overlay',
          widths[size],
        )}
      >
        {/*
          Swipe down to close, on the header only.

          Not the whole dialog: the body scrolls, and a downward drag there has
          to keep scrolling it rather than throwing the dialog away mid-read.
          The grabber says the header is the part you can pull; it shows only on
          phones, where the dialog is a bottom sheet and the gesture applies.
        */}
        <div {...dismissSwipe} className="touch-none pt-2 pb-1 sm:hidden">
          <div className="mx-auto h-1 w-9 rounded-full bg-[var(--color-line-strong)]" aria-hidden />
        </div>

        <header
          {...dismissSwipe}
          className="flex touch-none items-start justify-between gap-4 border-b border-[var(--color-line)] px-5 py-4"
        >
          <div>
            <h2 id={labelId} className="font-display text-lg font-semibold">
              {title}
            </h2>
            {description && <p className="mt-0.5 text-sm text-[var(--color-ink-soft)]">{description}</p>}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="-m-1 rounded-[var(--radius-sm)] p-1 text-[var(--color-ink-faint)] hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)]"
          >
            <X className="size-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer && (
          <footer className="flex justify-end gap-2 border-t border-[var(--color-line)] px-5 py-4 pb-safe">{footer}</footer>
        )}
      </div>
    </div>
  );
}

// --- Feedback --------------------------------------------------------------

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      {icon && <div className="mb-3 text-[var(--color-ink-faint)]">{icon}</div>}
      <h3 className="font-display text-base font-semibold text-[var(--color-ink)]">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-sm text-[var(--color-ink-soft)]">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('animate-pulse rounded-[var(--radius-md)] bg-[var(--color-surface-sunken)]', className)} />;
}

/**
 * A failure the screen could not recover from.
 *
 * Being offline is told apart from being broken, because they call for opposite
 * responses. Several screens — the dashboard, the inbox, payroll — are designed
 * to need the hub, and reporting that as "something went wrong" sent people
 * looking for a fault that was not there. It is not an error that the shop's
 * internet is down; it is a fact about what this screen can do right now.
 */
export function ErrorNote({ error, retry }: { error: unknown; retry?: () => void }) {
  const message = error instanceof Error ? error.message : String(error);
  const offline =
    (error as Error)?.name === 'OfflineUnavailableError' ||
    /not reachable|needs a connection|no connection/i.test(message);

  if (offline) {
    return (
      <div className="rounded-[var(--radius-md)] border border-[var(--color-warn)]/40 bg-[var(--color-warn-soft)] px-4 py-3 text-sm">
        <p className="font-medium text-[var(--color-warn)]">This screen needs a connection</p>
        <p className="mt-0.5 text-[var(--color-ink-soft)]">
          {message.replace(/^.*?(needs a connection.*)$/i, (_, tail) => tail as string)}
        </p>
        <p className="mt-2 text-xs text-[var(--color-ink-faint)]">
          Selling, parking a sale and looking a customer up all keep working meanwhile.
        </p>
        {retry && (
          <Button size="sm" variant="secondary" className="mt-2" onClick={retry}>
            Try again
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--color-danger)]/30 bg-[var(--color-danger-soft)] px-4 py-3 text-sm text-[var(--color-danger)]">
      <p className="font-medium">Something went wrong</p>
      <p className="mt-0.5 opacity-90">{message}</p>
      {retry && (
        <Button size="sm" variant="secondary" className="mt-2" onClick={retry}>
          Try again
        </Button>
      )}
    </div>
  );
}

// --- Toasts ----------------------------------------------------------------

export interface Toast {
  id: number;
  tone: Tone;
  title: string;
  body?: string;
  action?: { label: string; href?: string; onClick?: () => void };
}

const ToastContext = createContext<{ push: (toast: Omit<Toast, 'id'>) => void }>({ push: () => {} });

export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = (toast: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { ...toast, id }]);
    // Toasts carrying an action stay long enough to actually be used.
    setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), toast.action ? 12_000 : 5_000);
  };

  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      <div className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-full max-w-sm flex-col gap-2 pb-safe">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cx(
              'pointer-events-auto rounded-[var(--radius-md)] border px-4 py-3 elevation-raised',
              'border-[var(--color-line)] bg-[var(--color-surface-raised)]',
            )}
          >
            <div className="flex items-start gap-2">
              <Badge tone={toast.tone}>{toast.tone === 'ok' ? 'Done' : toast.tone === 'danger' ? 'Failed' : 'Note'}</Badge>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{toast.title}</p>
                {toast.body && <p className="mt-0.5 text-xs text-[var(--color-ink-soft)]">{toast.body}</p>}
                {toast.action &&
                  (toast.action.href ? (
                    <a
                      href={toast.action.href}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1.5 inline-block text-xs font-medium text-crust-600 underline"
                    >
                      {toast.action.label}
                    </a>
                  ) : (
                    <button onClick={toast.action.onClick} className="mt-1.5 text-xs font-medium text-crust-600 underline">
                      {toast.action.label}
                    </button>
                  ))}
              </div>
              <button
                onClick={() => setToasts((current) => current.filter((t) => t.id !== toast.id))}
                className="text-[var(--color-ink-faint)] hover:text-[var(--color-ink)]"
                aria-label="Dismiss"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// --- Tabs ------------------------------------------------------------------

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: { value: T; label: string; count?: number; tone?: Tone }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    /*
      Wraps on a phone rather than scrolling sideways.
      Settings has seven tabs needing about 610px; a phone gives 359px, so three
      of them — including "This device", where the server address is set — sat
      off the right edge behind a scroll with nothing to indicate it. Tabs that
      cannot be seen are tabs that do not exist. Sideways scrolling returns from
      `sm` up, where the strip fits on one line anyway.
    */
    <div
      role="tablist"
      className="flex flex-wrap gap-1 rounded-[var(--radius-md)] bg-[var(--color-surface-sunken)] p-1 sm:flex-nowrap sm:overflow-x-auto"
    >
      {tabs.map((tab) => (
        <button
          key={tab.value}
          role="tab"
          aria-selected={value === tab.value}
          onClick={() => onChange(tab.value)}
          className={cx(
            'flex shrink-0 items-center gap-1.5 rounded-[var(--radius-sm)] px-3 py-2 text-sm font-medium transition',
            value === tab.value
              ? 'bg-[var(--color-surface)] text-[var(--color-ink)] elevation-card'
              : 'text-[var(--color-ink-soft)] hover:text-[var(--color-ink)]',
          )}
        >
          {tab.label}
          {tab.count !== undefined && tab.count > 0 && (
            <span
              className={cx(
                'tabular rounded-full px-1.5 py-0.5 text-[11px] leading-none',
                tab.tone === 'danger' ? 'bg-[var(--color-danger)] text-white' : 'bg-crust-200 text-crust-800',
              )}
            >
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
